<template>
  <div class="pa-10">
    <title-page icon="mdi-magnify-scan">
      {{ $t('billing-control.gap-analysis') }}
    </title-page>

    <v-breadcrumbs
        :items="[
            {text: $t('reports.reports'), to: {name: 'reports'}, exact: true},
            {text: $t('billing-control.workspace'), to: {name: 'reports.billing-control'}, exact: true},
            {text: $t('billing-control.gap-analysis')},
        ]"
        divider=">"
    ></v-breadcrumbs>

    <!-- Tabs -->
    <v-tabs v-model="tab" @change="onTabChange">
      <v-tab v-for="t in tabs" :key="t" :href="'#' + t">
        {{ $t('billing-control.tabs.' + t) }}
        <help-tip :text="$t('billing-control.help.tabs.' + t)"/>
        <v-chip v-if="tabCounts[t] !== null && tabCounts[t] !== undefined" x-small class="ml-2">{{ tabCounts[t] }}</v-chip>
      </v-tab>
    </v-tabs>

    <!-- Filters + export -->
    <v-row align="center" class="mt-2">
      <v-col cols="12" md="4">
        <v-select
            :label="$t('billing-control.table.category')"
            :items="categoryItems"
            v-model="category"
            clearable
            dense
            hide-details
            outlined
            @change="reload"
        />
      </v-col>
      <v-spacer/>
      <v-col cols="auto">
        <v-btn text small @click="clearFilters">
          <v-icon left>mdi-filter-off-outline</v-icon>
          {{ $t('billing-control.actions.clear-filters') }}
        </v-btn>
        <v-btn outlined small color="primary" :loading="exporting" @click="exportResult">
          <v-icon left>mdi-download</v-icon>
          {{ $t('billing-control.actions.export') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Summary banner -->
    <v-alert dense text color="primary" class="mt-2">
      <b>{{ summary.organizations }}</b> {{ $t('billing-control.summary.organizations-selected') }} —
      <b>{{ numberFormat(summary.revenueImpact) }} €</b> {{ $t('billing-control.summary.total-impact') }}
      <help-tip :text="$t('billing-control.help.gap-summary')"/>
    </v-alert>

    <!-- Table -->
    <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{'items-per-page-options': [25, 50, 100]}"
        @click:row="openDetail"
        class="elevation-1"
    >
      <template v-for="(key, column) in explainedColumns" #[`header.${column}`]="{ header }">
        {{ header.text }} <help-tip :key="column" :text="$t('billing-control.help.table.' + key)"/>
      </template>
      <template #item.organization="{item}">
        {{ item.organizationName || item.organizationId || '—' }}
      </template>
      <template #item.category="{item}">
        {{ item.category ? $t('enums.subscription-plan-types.' + item.category) : '—' }}
      </template>
      <template #item.rootCause="{item}">{{ rootCauseLabel(item.rootCause) }}</template>
      <template #item.expected="{item}">{{ numberFormat(item.expected) }} €</template>
      <template #item.billed="{item}">{{ numberFormat(item.billed) }} €</template>
      <template #item.missing="{item}">
        <b v-if="isOverbilled(item)" class="warning--text">+{{ numberFormat(item.overbilled) }} € {{ $t('billing-control.overbilled') }}</b>
        <b v-else class="error--text">{{ numberFormat(item.missing) }} €</b>
      </template>
      <template #item.recoverable="{item}">
        <v-chip v-if="item.recoverable === true" x-small color="success" outlined>{{ $t('billing-control.leakage.recoverable') }}</v-chip>
        <v-chip v-else-if="item.recoverable === false" x-small outlined>{{ item.recoverableReason ? recoverableReasonLabel(item.recoverableReason) : $t('billing-control.leakage.non-recoverable') }}</v-chip>
        <span v-else>—</span>
      </template>
      <template #item.actions="{item}">
        <v-icon small @click.stop="openDetail(item)">mdi-eye-outline</v-icon>
      </template>
    </v-data-table>

    <!-- Drawer : Case Details -->
    <v-navigation-drawer v-model="drawer" right temporary fixed width="380">
      <div class="pa-4" v-if="detail">
        <div class="d-flex justify-space-between align-center">
          <h3>{{ $t('billing-control.drawer.title') }}</h3>
          <v-btn icon @click="drawer = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <div class="mt-4">
          <div class="caption text-uppercase grey--text">{{ $t('billing-control.table.organization') }}</div>
          <div class="font-weight-bold">{{ detail.organizationName || detail.organizationId || '—' }}</div>
        </div>
        <div class="mt-3">
          <div class="caption text-uppercase grey--text">{{ $t('billing-control.table.root-cause') }}</div>
          <div class="error--text">{{ detail.rootCause ? rootCauseLabel(detail.rootCause) : '—' }}</div>
        </div>
        <div class="mt-3">
          <div class="caption text-uppercase grey--text">{{ $t('billing-control.drawer.revenue-impact') }}</div>
          <div v-if="isOverbilled(detail)" class="text-h6 warning--text">+{{ numberFormat(detail.overbilled) }} € {{ $t('billing-control.overbilled') }}</div>
          <div v-else class="text-h6 primary--text">{{ numberFormat(detail.missing) }} €</div>
          <div class="caption">
            {{ $t('billing-control.table.expected') }}: {{ numberFormat(detail.expected) }} € ·
            {{ $t('billing-control.table.billed') }}: {{ numberFormat(detail.billed) }} €
          </div>
        </div>

        <v-divider class="my-3"/>
        <div class="caption text-uppercase grey--text">{{ $t('billing-control.drawer.billing-context') }}</div>
        <div class="d-flex justify-space-between mt-1"><span>{{ $t('billing-control.drawer.last-invoice') }}</span><b>{{ detail.lastInvoice || '—' }}</b></div>
        <div class="d-flex justify-space-between"><span>{{ $t('billing-control.drawer.billing-cycle') }}</span><b>{{ detail.billingCycle || '—' }}</b></div>
        <div class="d-flex justify-space-between"><span>{{ $t('billing-control.drawer.subscription-type') }}</span><b>{{ detail.subscriptionType ? $t('enums.subscription-plan-types.' + detail.subscriptionType) : '—' }}</b></div>

        <v-divider class="my-3"/>
        <div class="caption text-uppercase grey--text">{{ $t('billing-control.drawer.activity-history') }}</div>
        <v-timeline dense class="mt-2">
          <v-timeline-item v-for="(e, i) in detail.timeline" :key="i" small>
            <div>{{ $t('billing-control.timeline.' + e.key) }}</div>
            <div class="caption grey--text">{{ e.date }}</div>
          </v-timeline-item>
        </v-timeline>

        <v-row dense class="mt-2">
          <v-col cols="6">
            <v-btn small block :disabled="!organizationHref(detail)" :href="organizationHref(detail)" target="_blank">{{ $t('billing-control.actions.open-organization') }}</v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn small block :disabled="!subscriptionHref(detail)" :href="subscriptionHref(detail)" target="_blank">{{ $t('billing-control.actions.open-subscription') }}</v-btn>
          </v-col>
        </v-row>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import BillingControlRepository from "@repository/BillingControlRepository"
import HelpTip from "./HelpTip.vue"

export default {
  components: { HelpTip },

  data() {
    return {
      tabs: ['missing-requests', 'quantity-issues', 'pricing-issues', 'subscription-anomalies'],
      explainedColumns: {rootCause: 'root-cause', expected: 'expected', billed: 'billed', missing: 'missing', recoverable: 'recoverable'},
      tab: this.$route.query.tab || 'missing-requests',
      category: this.$route.query.category || null,
      items: [],
      total: 0,
      tabCounts: {},
      summary: {organizations: 0, revenueImpact: 0},
      loading: false,
      exporting: false,
      options: {},
      drawer: false,
      detail: null,
    }
  },

  computed: {
    headers() {
      return [
        {text: this.$t('billing-control.table.organization'), value: 'organization', sortable: false},
        {text: this.$t('billing-control.table.category'), value: 'category', sortable: false},
        {text: this.$t('billing-control.table.root-cause'), value: 'rootCause', sortable: false},
        {text: this.$t('billing-control.table.expected'), value: 'expected', align: 'end'},
        {text: this.$t('billing-control.table.billed'), value: 'billed', align: 'end'},
        {text: this.$t('billing-control.table.missing'), value: 'missing', align: 'end'},
        {text: this.$t('billing-control.table.recoverable'), value: 'recoverable', sortable: false},
        {text: this.$t('billing-control.table.actions'), value: 'actions', sortable: false, align: 'end'},
      ]
    },

    categoryItems() {
      return ['supervision', 'expense', 'energy-provider', 'connected-vehicle'].map(c => ({
        text: this.$t('enums.subscription-plan-types.' + c),
        value: c,
      }))
    },

    period() {
      const year = this.$route.query.year || new Date().getFullYear()
      return {fromDate: `${year}-01-01`, toDate: `${year}-12-31`}
    },
  },

  watch: {
    options: {deep: true, handler() { this.loadData() }},
  },

  methods: {
    loadData() {
      this.loading = true
      const data = {
        ...this.period,
        tab: this.tab,
        category: this.category,
        offset: this.options.itemsPerPage > 0 ? (this.options.page - 1) * this.options.itemsPerPage : 0,
        limit: this.options.itemsPerPage || 25,
        orderBy: (this.options.sortBy && this.options.sortBy[0]) || 'missing',
        orderDirection: (this.options.sortDesc && this.options.sortDesc[0]) ? 'desc' : 'asc',
      }
      BillingControlRepository.gaps(data).then(res => {
        this.items = res.items || []
        this.total = res.total || 0
        this.tabCounts = res.tabCounts || {}
        this.summary = res.summary || {organizations: 0, revenueImpact: 0}
        this.loading = false
      }).catch(err => {
        this.notifyError(err)
        this.loading = false
      })
    },

    reload() {
      this.options = {...this.options, page: 1}
    },

    onTabChange() {
      this.reload()
    },

    clearFilters() {
      this.category = null
      this.reload()
    },

    /** Rien ne manque, mais le client a payé plus que son abonnement : on le dit, plutôt qu'un 0 €. */
    isOverbilled(gap) {
      return !(gap.missing > 0) && gap.overbilled > 0
    },

    rootCauseLabel(value) {
      const map = {
        'Missing Request': 'missing-request',
        'Quantity Mismatch': 'quantity-mismatch',
        'Pricing Mismatch': 'pricing-mismatch',
      }
      return map[value] ? this.$t('billing-control.root-cause.' + map[value]) : value
    },

    recoverableReasonLabel(value) {
      const map = {
        'Expired Contract': 'expired-contract',
        'Non Billable Organization': 'non-billable-organization',
        'Exempt': 'exempt',
        'Closed Period': 'closed-period',
        'Missing Legal Data': 'missing-legal-data',
      }
      return map[value] ? this.$t('billing-control.recoverable-reason.' + map[value]) : value
    },

    openDetail(item) {
      if (!item.licenceId) return // anomalies have no licence-level detail
      this.showLoading(true)
      BillingControlRepository.gapDetail({
        ...this.period,
        subscriptionLicenceId: item.licenceId,
      }).then(detail => {
        this.detail = detail
        this.drawer = true
        this.showLoading(false)
      }).catch(err => {
        this.notifyError(err)
        this.showLoading(false)
      })
    },

    organizationHref(detail) {
      return detail.organizationId
          ? this.$router.resolve({name: 'organizations.subscriptions', params: {id: detail.organizationId}}).href
          : null
    },

    subscriptionHref(detail) {
      // Route imbriquée sous organizations/:id ⇒ il faut id ET subscriptionId.
      return detail.organizationId && detail.subscriptionId
          ? this.$router.resolve({name: 'organizations.subscriptions.edit', params: {id: detail.organizationId, subscriptionId: detail.subscriptionId}}).href
          : null
    },

    exportResult() {
      this.exporting = true
      BillingControlRepository.exportGaps({
        ...this.period,
        tab: this.tab,
        category: this.category,
      }).finally(() => {
        this.exporting = false
      })
    },
  },
}
</script>
